import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth, apiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Wordmark, Footer } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState({ phase: "verifying", email: null, activated: false, emailConfigured: true });
  const [supportEmail, setSupportEmail] = useState("support@warroomsolutions.example");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resent, setResent] = useState(false);
  const sid = useRef(new URLSearchParams(window.location.search).get("session_id"));

  useEffect(() => {
    api.get("/public/config").then(({ data }) => data?.support_email && setSupportEmail(data.support_email)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sid.current) { setState((s) => ({ ...s, phase: "error" })); return; }
    let tries = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/billing/status/${sid.current}`);
        if (data.payment_status === "paid" && data.provisioned) {
          setState({ phase: "ready", email: data.email, activated: !!data.activated, emailConfigured: !!data.email_configured });
          return;
        }
        if (data.payment_status === "failed" || data.status === "expired") {
          setState((s) => ({ ...s, phase: "failed" })); return;
        }
      } catch (e) { /* keep polling */ }
      if (++tries > 20) { setState((s) => ({ ...s, phase: "slow" })); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, []);

  const activate = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/activate", { session_id: sid.current, password });
      try { sessionStorage.removeItem("wr:intakeBannerDismissed"); } catch (_) { /* ignore */ }
      setUser(data);
      toast.success("Welcome to your WarRoom.");
      navigate(data.onboarding_complete ? "/app/today" : "/intake");
    } catch (err) {
      setError(apiError(err));
      setBusy(false);
    }
  };

  const resend = async () => {
    setResent(false);
    try {
      const { data } = await api.post("/billing/resend-activation", { session_id: sid.current });
      toast.success(data.message || "Activation email sent.");
      setResent(true);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-wr-bg flex flex-col wr-grid-bg" data-testid="payment-success-page">
      <header className="border-b border-wr-border bg-black/70 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 h-16 flex items-center"><Wordmark size="md" /></div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg bg-wr-surface border border-wr-border p-8 wr-fade-up">
          {state.phase === "verifying" && (
            <div data-testid="pay-verifying">
              <Loader2 className="h-8 w-8 text-wr-red animate-spin" />
              <h1 className="wr-heading font-bold text-white text-2xl mt-4">Confirming your payment…</h1>
              <p className="text-sm text-wr-sub mt-2">Verifying your subscription with Stripe and preparing your WarRoom.</p>
            </div>
          )}

          {state.phase === "ready" && !state.activated && (
            <div data-testid="pay-activate">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
              <h1 className="wr-heading font-bold text-white text-2xl mt-4">Payment confirmed. Your WarRoom is ready.</h1>
              <p className="text-sm text-wr-sub mt-2">Create your password to sign in and start Sarge's first-day intake.</p>
              <div className="mt-4 border border-wr-border bg-wr-bg p-3">
                <p className="wr-label">Account email</p>
                <p className="wr-mono text-sm text-white break-all" data-testid="pay-email">{state.email}</p>
              </div>
              <form onSubmit={activate} className="mt-5 space-y-4" data-testid="pay-activate-form">
                <div>
                  <label className="wr-label">Create password</label>
                  <Input required type="password" data-testid="pay-password" value={password} minLength={8}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
                </div>
                <div>
                  <label className="wr-label">Confirm password</label>
                  <Input required type="password" data-testid="pay-confirm" value={confirm} minLength={8}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
                </div>
                {error && <p className="text-sm text-wr-red wr-mono" data-testid="pay-error">{error}</p>}
                <Button type="submit" disabled={busy} data-testid="pay-activate-submit"
                  className="w-full rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-11">
                  {busy ? "Setting up…" : "Set password & enter WarRoom"}
                </Button>
              </form>
              <div className="mt-5 border-t border-wr-border pt-4">
                <p className="text-xs text-wr-muted flex items-start gap-2">
                  <Mail size={14} className="mt-0.5 shrink-0 text-wr-sub" />
                  {state.emailConfigured
                    ? <>Prefer email? We've sent a secure “Set your password” link to your inbox.{" "}
                        <button type="button" onClick={resend} data-testid="pay-resend"
                          className="underline hover:text-white">Resend it</button>{resent && <span className="text-emerald-400"> · sent</span>}</>
                    : <>You can also finish signing in later — closing this page won't lock you out. Contact{" "}
                        <a href={`mailto:${supportEmail}`} className="underline hover:text-white">{supportEmail}</a> if you need help.</>}
                </p>
                <p className="text-[0.7rem] text-wr-muted mt-3">14-day money-back guarantee on your first payment. Cancel anytime after that; access continues to the end of your paid term.</p>
              </div>
            </div>
          )}

          {state.phase === "ready" && state.activated && (
            <div data-testid="pay-already-activated">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
              <h1 className="wr-heading font-bold text-white text-2xl mt-4">Your WarRoom is active.</h1>
              <p className="text-sm text-wr-sub mt-2">This account already has a password. Sign in to continue — or use “Forgot password” if you need to reset it.</p>
              <Button onClick={() => navigate("/login")} data-testid="pay-signin"
                className="mt-6 w-full rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-11">Sign in to your WarRoom</Button>
            </div>
          )}

          {state.phase === "slow" && (
            <div data-testid="pay-slow">
              <h1 className="wr-heading font-bold text-white text-2xl">Still processing…</h1>
              <p className="text-sm text-wr-sub mt-2">Your payment is being finalized. We've emailed a secure “Set your password” link — it will arrive shortly and your WarRoom will be ready.</p>
              <Button onClick={() => navigate("/login")} className="mt-6 rounded-none bg-wr-red text-white wr-mono uppercase h-11 px-6">Go to sign in</Button>
            </div>
          )}

          {(state.phase === "failed" || state.phase === "error") && (
            <div data-testid="pay-failed">
              <h1 className="wr-heading font-bold text-white text-2xl">We couldn't confirm payment.</h1>
              <p className="text-sm text-wr-sub mt-2">No WarRoom was created. If you were charged, contact support and nothing further will happen automatically.</p>
              <Button onClick={() => navigate("/")} className="mt-6 rounded-none border border-wr-border bg-transparent text-white wr-mono uppercase h-11 px-6">Back to home</Button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
